import { Fragment, MouseEvent, MouseEventHandler, useEffect, useState } from 'react';

import { connect } from 'react-redux';

import AppBarInternal from '@o2/common-business/AppBarInternal';

import { CheckMark, Cross, NewMessage } from '@o2/icons';

import UserPopover from './UserPopover';
import { environment } from 'environments/environment';
import { ExternalUserAccesses } from 'generated/bffUserAccess/models';
import { Link } from 'generated/bslOmniShellNav/models';
import { PartnerContract, SharedObjectsData } from 'generated/sharedObjects/models';
import { AppState } from 'helpers/store/models/AppState';
import { useHasSimpleUserPrivileges } from 'hooks/useHasSimpleUserPrivileges';
import { useTranslationWithDataKey } from 'hooks/useTranslationWithDataKey';
import { ClearContextEntityType, ClearContextRequestActionInputs } from 'models/context/ClearContext';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { ExternalUser } from 'models/context/ExternalUser';
import { UserOrganizationUnit, UserRole } from 'models/context/InternalUser';
import { LegacyCrmDeeplinkCommand, LegacyCrmDeeplinkConfig } from 'models/LegacyCrmDeeplink';
import { getExternalUserAccessesAction, selectAccessExternalUserAccessesData } from 'saga/accessManagement/ducks';
import { isProdEnvironment } from 'saga/appHandlers/ducks';
import { AuthUserType, selectAuthUserType } from 'saga/auth/ducks';
import { selectContextCustomerInfo } from 'saga/context/customer/ducks';
import { clearContextAction } from 'saga/context/ducks';
import { selectContextExternalUser } from 'saga/context/external/ducks';
import {
  getNavigationLinksAction,
  selectContextInternalActiveOrganizationUnit,
  selectContextInternalIsUserScreenFilterDisabled,
  selectContextInternalOrganizationUnits,
  selectContextInternalRoles,
  selectContextInternalSelectedRole,
  selectContextInternalUserName,
  selectNavigationLinks,
  setExternalScreenFilterAction,
  setPrimaryOUActions,
  setSelectedRoleActions,
} from 'saga/context/internal/ducks';
import { selectContextOrderInfo } from 'saga/context/partnerContract/ducks';
import { redirectToLegacyCrmAction } from 'saga/crm-deeplinks/ducks';
import { selectProperty } from 'saga/property/ducks';
import { selectSharedObjectsData } from 'saga/sharedObjects/ducks';
import HasSimpleUserPrivilege from 'views/components/ACL/HasSimpleUserPrivilege';

export interface PopoverState {
  id: string | null; // what popover to open
  target: EventTarget | null; // where to open it (anchor)
}

const mySalesUrlProperty = 'gui.internalHeader.action.mySalesUrl';

const NewAppBarInternal = (props: StateToProps & DispatchToProps) => {
  const { t } = useTranslationWithDataKey();
  const availablePrivileges = useHasSimpleUserPrivileges(['REDIRECT_OMNI']);
  const { REDIRECT_OMNI } = availablePrivileges;

  const missingName = environment.properties['gui.general.BFFDIGITVCAPTURE.contactPerson.missingName'];

  useEffect(() => {
    if (REDIRECT_OMNI) {
      props.getNavigationLinks();
    }
  }, [REDIRECT_OMNI]);

  const handlePopoverClose = () => {
    setPopoverElm({
      id: null,
      target: null,
    });
  };

  const [popoverElm, setPopoverElm] = useState<PopoverState>({
    id: null,
    target: null,
  });

  const handleOUChange = (ouRefNo: string) => () => {
    props.setPrimaryOU(ouRefNo);
  };

  const handleRoleChange = (role: string) => () => {
    props.setSelectedRole(role);
  };

  const handleMySalesClick = () => {
    window.open(props.mySalesURL, '_blank');
  };

  const handleSwitch = () => {
    props.setExternalScreenFilter(!props.isScreenFilterEnabled);
  };

  const handleInternalUserClick = () => {
    if (props.authUserType === 'internal') {
      props.redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: { page: 'internalUserDetail' },
      });
    }
  };

  const handleHomeIconClick = () => {
    if (props.authUserType === 'internal') {
      props.redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: { page: 'internalDashboard' },
      });
    }
  };

  /*eslint-disable */
  const handlePopoverOpen =
    (i: string): MouseEventHandler =>
    (e) => {
      if (popoverElm?.id === i) {
        return handlePopoverClose();
      }

      setPopoverElm({
        id: i,
        target: e.currentTarget,
      });
    };
  /*eslint-enable */

  const handleUserPopoverOpen = (evt: MouseEvent) => {
    if (props.contextExternalUser?.cpRefNo) {
      if (!props.userAccesses) {
        props.getExternalUserAccesses(props.contextExternalUser.cpRefNo);
      }

      handlePopoverOpen('userPopover')(evt);
    } else if (props.contextCustomer) {
      if (props.authUserType === 'internal') {
        props.redirectToLegacyCrm({
          command: LegacyCrmDeeplinkCommand.IMO2DASH,
          queryParams: {
            page: 'searchContactPersonResults',
            action: 'searchContactPerson',
          },
        });
      }
    } else if (props.authUserType === 'internal') {
      props.redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: { page: 'internalDashboard' },
      });
    }
  };

  const handleMessageClick = () => {
    if (props.authUserType === 'internal') {
      props.redirectToLegacyCrm({
        command: LegacyCrmDeeplinkCommand.IMO2DASH,
        queryParams: { page: 'manualNotification' },
      });
    }
  };

  const handlePartnerContractRedirect = () => {
    window.location.href = environment.properties['mo2care.intDashb.partnerprogram.url'];
  };

  const handleClearingSessionPartnerContract = (partnerContract: ClearContextEntityType) => {
    props.clearContext({ clearContextEntity: partnerContract });
  };

  const partnerContractIsNotEmptyOrIsNotNull: boolean =
    props.contextPartnerContract?.obligationRefNo !== '' ||
    props.contextPartnerContract?.marketingOfferCode !== '' ||
    props.contextPartnerContract?.marketingOfferName !== '';

  return (
    <AppBarInternal
      environment={props.isProd ? 'production' : 'pre-production'}
      onToggleInternalContent={handleSwitch}
      initialOpen={!props.isScreenFilterEnabled}
      onHomeButtonClick={handleHomeIconClick}
      eyeButtonTooltip={t('onecrm.gui_internalHeader.action.eyeButtonTooltip', false)}
      homeButtonTooltip={t('onecrm.gui_internalHeader.label.homeButtonTooltip', false)}
    >
      <AppBarInternal.Controls rightDivider>
        <AppBarInternal.Button data-elm="11.1.4_007" onClick={handleInternalUserClick}>
          {props.userName}
        </AppBarInternal.Button>
        <AppBarInternal.DropDownButton
          data-elm="11.1.4_025a"
          menuElmId="11.1.4_025b"
          label={props.activeOU?.name}
          title={t('onecrm.gui_entityManagement.internal_user_settings.label.ouHeading', false)}
        >
          {props.organisationUnits
            .filter((ou) => ou.isPrimary)
            .map((ou) => (
              <AppBarInternal.DropDownItem key={ou.ouRefNo} onClick={handleOUChange(ou.ouRefNo)}>
                {ou.name}
                {ou.isPrimary && <CheckMark />}
              </AppBarInternal.DropDownItem>
            ))}
          {props.organisationUnits
            .filter((ou) => !ou.isPrimary)
            .map((ou) => (
              <AppBarInternal.DropDownItem key={ou.ouRefNo} onClick={handleOUChange(ou.ouRefNo)}>
                {ou.name}
                {ou.isPrimary && <CheckMark />}
              </AppBarInternal.DropDownItem>
            ))}
        </AppBarInternal.DropDownButton>
        <AppBarInternal.DropDownButton
          data-elm="11.1.4_029a"
          menuElmId="11.1.4_029b"
          label={t(`oneportal.lov.onecrm.internalUserRole.${props.selectedRole?.roleType}`)}
          title={t('onecrm.gui_entityManagement.internal_user_settings.label.roles', false)}
        >
          {props.roles
            .filter((role) => role.isPrimary)
            .map((role) => (
              <AppBarInternal.DropDownItem key={role.roleType} onClick={handleRoleChange(role.roleType)}>
                {t(`oneportal.lov.onecrm.internalUserRole.${role.roleType}`)}
                {role.isPrimary && <CheckMark />}
              </AppBarInternal.DropDownItem>
            ))}
          {props.roles
            .filter((role) => !role.isPrimary)
            .map((role) => (
              <AppBarInternal.DropDownItem key={role.roleType} onClick={handleRoleChange(role.roleType)}>
                {t(`oneportal.lov.onecrm.internalUserRole.${role.roleType}`)}
                {role.isPrimary && <CheckMark />}
              </AppBarInternal.DropDownItem>
            ))}
        </AppBarInternal.DropDownButton>
        <HasSimpleUserPrivilege privilegeType="REDIRECT_OMNI">
          <AppBarInternal.Button data-elm="11.1.4_038" onClick={handleMySalesClick}>
            {t('onecrm.gui_entityManagement.internal_user_settings.label.mySales', false)}
          </AppBarInternal.Button>
        </HasSimpleUserPrivilege>
        <HasSimpleUserPrivilege privilegeType="REDIRECT_OMNI">
          {props.navigationLinks?.length > 0 && (
            <AppBarInternal.DropDownButton
              data-elm="11.1.4_039"
              menuElmId="11.1.4_029b"
              label={t('onecrm.gui_entityManagement.internal_user_settings.label.omniViews', false)}
              title={t('onecrm.gui_entityManagement.internal_user_settings.label.omniViews', false)}
            >
              {props.navigationLinks?.map((link: Link) => (
                <AppBarInternal.DropDownItem key={link.title} onClick={() => window.open(link.url, '_blank')}>
                  {link.title}
                </AppBarInternal.DropDownItem>
              ))}
            </AppBarInternal.DropDownButton>
          )}
        </HasSimpleUserPrivilege>
      </AppBarInternal.Controls>
      <AppBarInternal.Controls leftDivider>
        {!props.isScreenFilterEnabled && (
          <>
            {partnerContractIsNotEmptyOrIsNotNull && (
              <>
                <AppBarInternal.Button
                  data-elm="11.1.4_030"
                  onClick={handlePartnerContractRedirect}
                  title={t('onecrm.gui_internalHeader.partnerContract.marketingOfferName', false, {
                    0: props.contextPartnerContract?.marketingOfferName,
                  })}
                >
                  {t('onecrm.gui_internalHeader.partnerContract.marketingOfferName', false, {
                    0: props.contextPartnerContract?.marketingOfferName,
                  })}
                </AppBarInternal.Button>
                <AppBarInternal.Button
                  data-elm="11.1.4_031"
                  onClick={() => handleClearingSessionPartnerContract(ClearContextEntityType.PARTNER_CONTRACT)}
                >
                  <Cross fontSize="inherit" />
                </AppBarInternal.Button>
              </>
            )}
            {props.contextExternalUser && props.contextExternalUser?.isVerified !== undefined ? (
              <AppBarInternal.UserButton
                aria-describedby="userPopover"
                aria-haspopup="true"
                data-elm="11.1.4_021"
                onClick={handleUserPopoverOpen}
                verified={props.contextExternalUser.isVerified}
                verifiedStatus={
                  props.sharedObjectData?.externalUser?.verifiedUser
                    ? props.sharedObjectData.externalUser.verificationMethod === 'mobileApp'
                      ? t('onecrm.gui_internalHeader.label.mobileAppVerified', false)
                      : t('onecrm.gui_internalHeader.label.cpVerified2', false)
                    : t('onecrm.gui_internalHeader.label.cpUnverified2', false)
                }
                hasContactPerson
                open={Boolean(popoverElm.id)}
              >
                <Fragment data-elm="11.1.4_022">
                  {props.contextExternalUser &&
                    (props.contextExternalUser.firstName === missingName
                      ? props.contextExternalUser.email
                      : props.contextExternalUser.displayName)}
                </Fragment>
              </AppBarInternal.UserButton>
            ) : (
              <AppBarInternal.UserButton data-elm="11.1.4_021" onClick={handleUserPopoverOpen} hasContactPerson={false}>
                {t('onecrm.gui_internalHeader.label.cpNotSet')}
              </AppBarInternal.UserButton>
            )}
            {props.contextCustomer && (
              <HasSimpleUserPrivilege privilegeType="NOTIFICATION_CREATE">
                <AppBarInternal.Button
                  data-elm="11.1.4_002"
                  onClick={handleMessageClick}
                  title={t('onecrm.gui_internalHeader.label.notificationButtonTooltip', false)}
                >
                  <NewMessage />
                </AppBarInternal.Button>
              </HasSimpleUserPrivilege>
            )}
          </>
        )}
      </AppBarInternal.Controls>

      {props.contextExternalUser && (
        <UserPopover id="userPopover" popoverElm={popoverElm} handlePopoverClose={handlePopoverClose} />
      )}
    </AppBarInternal>
  );
};

interface StateToProps {
  userName: string;
  organisationUnits: UserOrganizationUnit[];
  activeOU?: UserOrganizationUnit;
  roles: UserRole[];
  selectedRole?: UserRole;
  contextExternalUser: ExternalUser | null;
  contextCustomer: CustomerInfo | null;
  isScreenFilterEnabled: boolean;
  authUserType: AuthUserType;
  userAccesses: ExternalUserAccesses | null;
  isProd: boolean;
  isInternal: boolean;
  contextPartnerContract: PartnerContract | null;
  navigationLinks: Link[];
  mySalesURL: string;
  sharedObjectData: SharedObjectsData | null;
}

interface DispatchToProps {
  setPrimaryOU: (id: string) => void;
  setSelectedRole: (id: string) => void;
  getExternalUserAccesses: (payload: string) => void;
  setExternalScreenFilter: (screenFilter: boolean) => void;
  redirectToLegacyCrm: (deeplinkConfig: LegacyCrmDeeplinkConfig) => void;
  clearContext: (payload: ClearContextRequestActionInputs) => void;
  getNavigationLinks: () => void;
}

const mapStateToProps = (state: AppState): StateToProps => ({
  userName: selectContextInternalUserName(state),
  organisationUnits: selectContextInternalOrganizationUnits(state),
  activeOU: selectContextInternalActiveOrganizationUnit(state),
  roles: selectContextInternalRoles(state),
  selectedRole: selectContextInternalSelectedRole(state),
  contextExternalUser: selectContextExternalUser(state),
  contextCustomer: selectContextCustomerInfo(state),
  isScreenFilterEnabled: !selectContextInternalIsUserScreenFilterDisabled(state),
  authUserType: selectAuthUserType(state),
  userAccesses: selectAccessExternalUserAccessesData(state),
  isProd: isProdEnvironment(),
  isInternal: selectContextInternalIsUserScreenFilterDisabled(state),
  contextPartnerContract: selectContextOrderInfo(state),
  navigationLinks: selectNavigationLinks(state),
  mySalesURL: selectProperty(mySalesUrlProperty),
  sharedObjectData: selectSharedObjectsData(state),
});

const mapDispatchToProps: DispatchToProps = {
  setExternalScreenFilter: setExternalScreenFilterAction.request,
  setPrimaryOU: setPrimaryOUActions.request,
  setSelectedRole: setSelectedRoleActions.request,
  getExternalUserAccesses: getExternalUserAccessesAction.request,
  redirectToLegacyCrm: redirectToLegacyCrmAction,
  clearContext: clearContextAction.request,
  getNavigationLinks: getNavigationLinksAction.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(NewAppBarInternal);
